import React, { KeyboardEvent, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";
import { connect, useDispatch } from "react-redux";
import { PatchedSupportedBrand } from "checkout/ts/redux/models/configuration";
import { PatchedTokenCardData, ThreeDsStatus } from "checkout/ts/redux/sdk/resources/TransactionTokens";
import { parsePhone } from "checkout/ts/redux/utils/intl";
import {
    isTransactionThreeDsAddressRequired,
    isTransactionThreeDsPhoneNumberRequired,
} from "checkout/ts/redux/utils/token";
import { cardBrandAPIName } from "checkout/ts/utils/cards";
import { getCheckoutParams } from "checkout/ts/utils/checkout-params";
import { isDarkTheme } from "checkout/ts/utils/colors";
import { validationToErrors } from "checkout/ts/utils/errors";
import { UnivaMetadata } from "checkout/ts/utils/metadata";
import { Regexes } from "checkout/ts/utils/Regexes";
import classnames from "classnames";
import { Countries } from "common/locale/constants";
import { STATES_BY_COUNTRY } from "common/locale/states";
import { InlineSplitElement, InlineStyleKeys, NewCardBrand } from "common/types";
import { needsCvv } from "common/validation/card";
import { has, isEmpty } from "lodash";
import Payment from "payment";
import { generate } from "randomstring";
import { compose } from "recompose";
import {
    blur,
    Form,
    FormSection,
    formValueSelector,
    getFormMeta,
    getFormSyncErrors,
    InjectedFormProps,
    reduxForm,
} from "redux-form";
import { PaymentType, TransactionTokenCardDataItem, TransactionTokenType } from "univapay-node";

import { FORM_CHECKOUT_NAME, FORM_TOKEN_SELECT_NAME } from "../../../../../common/constants";
import { LOCALE_LABELS } from "../../../locale/labels";
import { Dispatch, StateShape, store } from "../../../redux/store";
import { amountForCurrency } from "../../../utils/money";
import { FormCheckoutData } from "../../checkout/FormCheckout";
import { SpinnerLoader } from "../../common/SpinnerLoader";
import { CheckboxField } from "../../forms/Checkbox";
import { PhoneNumberField } from "../../forms/PhoneNumberInput";
import { TextField } from "../../forms/Text";
import { CardCVVInput } from "../../forms/text/CardCVVInput";
import { CardExpInput } from "../../forms/text/CardExpInput";
import { CardNumberInput } from "../../forms/text/CardNumberInput";
import { TestEnvironmentBanner } from "../../layout/TestEnvironmentBanner";
import { BillingAddressEn } from "../common/FormAddress/BillingAddressEn";
import { BillingAddressJa } from "../common/FormAddress/BillingAddressJa";
import { PrivacyLink } from "../common/FormData";
import { getTokens } from "../common/FormTokenSelector";
import { hasInvalidAddressField } from "../common/utils/customerInfo";
import { obfuscateCardNumber, obfuscateCvv } from "../common/utils/obfuscate";

import { InstallmentSelectField } from "./components/InstallmentSelectField";
import { validatePhoneNumber } from "./utils/phoneNumber";
import { validateEmail } from "./utils/validateEmail";
import FormCardSelector from "./FormCardSelector";
import { CardDataRaw, FormDataCardData, generateValidation, normalizeValues } from "./FormData";
import { FormDataCard } from "./index";

const stateSelector = (state: StateShape) => {
    const { application, product, configuration, checkout, userData } = state;

    const {
        address = false,
        requireEmail = address,
        requirePhoneNumber,
        requireBillingAddress,
        email,
        customerName,
        customerNameKana,
        inlineStyles,
        inlineBaseFontSize,
        allowCardInstallments,
        horizontalInlineLayout,
        hideRecurringCheckbox,
        hidePrivacyLink,

        shippingAddressCity,
        shippingAddressCountryCode,
        shippingAddressLine1,
        shippingAddressLine2,
        shippingAddressState,
        shippingAddressZip,
        phoneNumber: prefilledPhoneNumber,
        cardholder: prefilledCardholder,
        exp: prefilledExp,
        callCenterTokenId,
        placeholderCardNumberLastFour,
        placeholderCardBrand,
    } = application.params.params;

    const {
        tokenType,
        currency,
        initialAmount,
        univapayCustomerId,
        isSubscription,
        inlineSplitId,
        inlineSplitElements,
    } = getCheckoutParams(application.params, product.products, configuration.data);

    const { tokens } = state.tokens;

    const { connector, invalidFields } = application;

    const selectedTokenId = formValueSelector(FORM_TOKEN_SELECT_NAME)(state, "selectedTokenId");
    const selectedToken = tokens?.[selectedTokenId];

    const cardInfoSelector = formValueSelector(FORM_CHECKOUT_NAME);
    const cardNumber = cardInfoSelector(state, "data.cardNumber");
    const installmentCycles = cardInfoSelector(state, "installmentCycles");
    const cardType = cardNumber ? cardBrandAPIName(Payment.fns.cardType(cardNumber)) : null;

    const brands = configuration.paymentMethods.find(({ key }) => key === checkout.paymentMethodKey)?.brands || [];
    const supportsPrivateLabel = brands.some((brand) => brand === NewCardBrand.PRIVATE_LABEL);
    const forceRecurringToken = tokenType === TransactionTokenType.RECURRING;

    const hasInstallmentCapableBrands = ((configuration.data?.supportedBrands as PatchedSupportedBrand[]) || []).some(
        ({ brand, cardBrand, installmentCapable }) => brands.includes(brand || cardBrand) && installmentCapable
    );

    const showInstallmentSelect =
        hasInstallmentCapableBrands &&
        !isSubscription &&
        allowCardInstallments &&
        configuration.data?.installmentsConfiguration?.enabled;

    const errors = getFormSyncErrors(FORM_CHECKOUT_NAME)(state);
    // formMeta is storing touched fields
    const formMeta = getFormMeta(FORM_CHECKOUT_NAME)(state);

    const filterUntouchedValues = (
        values: Record<string, unknown>,
        formMeta: Record<string, { touched: boolean } | Record<string, { touched: boolean }>>
    ) =>
        Object.keys(values).reduce((acc, key) => {
            const value = values[key];
            const keyMetadata = has(formMeta, key) && formMeta[key];

            if (value && typeof value === "object") {
                const values = filterUntouchedValues(
                    value as Record<string, unknown>,
                    keyMetadata as Record<string, { touched: boolean }>
                );

                if (!isEmpty(values)) {
                    acc[key] = values;
                }
            } else if (keyMetadata?.touched) {
                acc[key] = value;
            }

            return acc;
        }, {});

    // picking up the fields that is touched and have validation error
    const validationErrors = filterUntouchedValues(errors as Record<string, unknown>, formMeta) as {
        data?: Record<string, string>;
    };

    const isCurrentBrandInstallmentCapable =
        !cardType ||
        (configuration.data?.supportedBrands || []).some(
            ({ brand, cardBrand, installmentCapable }) => (brand || cardBrand) === cardType && installmentCapable
        );

    const threeDsPhoneNumberRequired = isTransactionThreeDsPhoneNumberRequired(
        application.params.params,
        configuration.data
    );
    const threeDsAddressRequired = isTransactionThreeDsAddressRequired(application.params.params, configuration.data);
    const isCustomerIdTokenAuthorized =
        !!selectedToken && (selectedToken?.data as PatchedTokenCardData)?.threeDs?.status === ThreeDsStatus.SUCCESSFUL;

    const requireThreeDsAddress = !isCustomerIdTokenAuthorized && threeDsAddressRequired;
    const requireThreeDsPhone = !isCustomerIdTokenAuthorized && threeDsPhoneNumberRequired;

    const countryCode = shippingAddressCountryCode?.toUpperCase();

    const country = Countries[countryCode]
        ? countryCode
        : Countries[(selectedToken?.data as TransactionTokenCardDataItem)?.billing?.country]
        ? (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.country
        : Countries[selectedToken?.metadata?.[UnivaMetadata.ADDRESS_COUNTRY]]
        ? selectedToken?.metadata?.[UnivaMetadata.ADDRESS_COUNTRY]
        : requireThreeDsAddress || requireBillingAddress
        ? "JP"
        : undefined;

    const availableStates = country ? STATES_BY_COUNTRY[country.toLocaleUpperCase()] : undefined;
    const queryState =
        shippingAddressState ||
        (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.state ||
        selectedToken?.metadata?.[UnivaMetadata.ADDRESS_STATE];

    const addressState =
        availableStates && queryState && requireThreeDsAddress
            ? availableStates.find(
                  (state) =>
                      state.name.toLowerCase() === queryState.toLowerCase() ||
                      (state.label && queryState.toLowerCase().includes(state.label.toLowerCase()))
              )?.name
            : queryState;

    const hasPrefilledAddress =
        !!shippingAddressCity &&
        !!shippingAddressCountryCode &&
        !!shippingAddressLine1 &&
        !!addressState &&
        !!shippingAddressZip;

    const showRecurringAccept = !hideRecurringCheckbox && (forceRecurringToken || !!univapayCustomerId);
    const splitIdShowRecurringAccept =
        inlineSplitElements?.includes(InlineSplitElement.DATA_ACCEPT_RECURRING) ||
        (inlineSplitElements?.includes(InlineSplitElement.DATA_ACCEPT_RECURRING_OPTIONAL) && showRecurringAccept);
    const isAcceptVisible = inlineSplitId ? splitIdShowRecurringAccept : showRecurringAccept;

    return {
        updateFlowShouldCreateNewToken: state.checkout.updateFlowShouldCreateNewToken,
        cardBrand: cardType,
        isCurrentBrandInstallmentCapable,
        installmentCycles,
        requireEmail: inlineSplitId
            ? inlineSplitElements?.includes(InlineSplitElement.EMAIL)
            : invalidFields.email?.showField || requireEmail || !email,
        requireCvv: inlineSplitId
            ? inlineSplitElements?.includes(InlineSplitElement.DATA_CVV)
            : needsCvv(state, selectedTokenId),
        currency,
        subscriptionInitialAmount: initialAmount,
        tokenType,
        univapayCustomerId,
        isSubscription,
        tokens,
        selectedTokenId,
        selectedToken,
        fetched: !!tokens && !!state.configuration.data,
        inlineStyles,
        inlineBaseFontSize,
        initialValues: {
            email: email || selectedToken?.email,
            name: customerName,
            nameKana: customerNameKana,
            data: {
                line1:
                    shippingAddressLine1 ||
                    (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.line1 ||
                    selectedToken?.metadata?.[UnivaMetadata.ADDRESS_LINE1],
                line2:
                    shippingAddressLine2 ||
                    (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.line2 ||
                    selectedToken?.metadata?.[UnivaMetadata.ADDRESS_LINE2],
                city:
                    shippingAddressCity ||
                    (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.city ||
                    selectedToken?.metadata?.[UnivaMetadata.ADDRESS_CITY],
                state: addressState,
                zip:
                    shippingAddressZip ||
                    (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.zip ||
                    selectedToken?.metadata?.[UnivaMetadata.ADDRESS_ZIP],
                country,
                phoneNumber:
                    parsePhone(prefilledPhoneNumber) ||
                    (selectedToken?.data as TransactionTokenCardDataItem)?.billing?.phoneNumber ||
                    parsePhone(selectedToken?.metadata?.[UnivaMetadata.PHONE_NUMBER]),
                cardholder: prefilledCardholder,
                exp: prefilledExp,
            },
            installmentCycles: showInstallmentSelect ? 1 : undefined,
        },
        showInstallmentSelect: inlineSplitId
            ? inlineSplitElements?.includes(InlineSplitElement.DATA_CARD_INSTALLMENTS) && showInstallmentSelect
            : showInstallmentSelect,
        darkTheme: isDarkTheme(state),
        horizontalInlineLayout,
        supportsPrivateLabel,
        brands,
        requireName: inlineSplitId ? inlineSplitElements?.includes(InlineSplitElement.NAME) : false,
        requireNameKana: inlineSplitId ? inlineSplitElements?.includes(InlineSplitElement.NAME_KANA) : false,
        requirePhoneNumber:
            invalidFields.phoneNumber?.showField ||
            requirePhoneNumber ||
            (requirePhoneNumber !== false && address === true),
        requireThreeDsPhoneNumber: requireThreeDsPhone && (!prefilledPhoneNumber || requirePhoneNumber !== false),
        requireAddress: hasInvalidAddressField(invalidFields) || requireBillingAddress || address,
        requireThreeDsAddress: requireThreeDsAddress && (requireBillingAddress !== false || !hasPrefilledAddress),
        requireCardholder: inlineSplitId
            ? inlineSplitElements?.includes(InlineSplitElement.DATA_CARDHOLDER_NAME)
            : true,
        requireCardNumber: inlineSplitId ? inlineSplitElements?.includes(InlineSplitElement.DATA_CARD_NUMBER) : true,
        requireExpiry: inlineSplitId ? inlineSplitElements?.includes(InlineSplitElement.DATA_EXPIRY) : true,
        validationErrors,
        connector,
        isAcceptRequired: isAcceptVisible && forceRecurringToken,
        isAcceptVisible: inlineSplitId ? splitIdShowRecurringAccept : showRecurringAccept,
        hidePrivacyLink: inlineSplitId
            ? !inlineSplitElements?.includes(InlineSplitElement.DATA_PRIVACY_POLICY)
            : hidePrivacyLink,
        callCenterTokenId,
        placeholderCardNumberLastFour,
        placeholderCardBrand,
        formMeta,
        invalidFields,
        userData,

        splitCheckoutConnector: state.application.splitCheckoutConnector,
    };
};

type OwnProps = InjectedFormProps<FormDataCardData<CardDataRaw>> & ReturnType<typeof stateSelector>;

const validate = (values: Partial<FormDataCardData<CardDataRaw>>, props: OwnProps) => {
    const baseValidationObject = generateValidation(values, props);

    const {
        requireEmail,
        requirePhoneNumber,
        requireAddress,
        selectedTokenId,
        requireThreeDsPhoneNumber,
        requireThreeDsAddress,
        requireName,
        requireNameKana,
    } = props;
    const { name, nameKana, email, data: { phoneNumber, line1, city, state, zip, country } = {} } = values;

    const isAddressRequired = requireAddress || requireThreeDsAddress;

    return validationToErrors({
        ...baseValidationObject,
        "name": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireName && isEmpty(name),
        },
        "nameKana": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireNameKana && isEmpty(nameKana),
            [LOCALE_LABELS.VALIDATION_INVALID_CHARACTERS]:
                requireNameKana && nameKana && !Regexes.Katakana.test(nameKana),
        },
        "email": !selectedTokenId && validateEmail(email, requireEmail),
        "data.phoneNumber.localNumber": {
            // inline checkout validation support
            [LOCALE_LABELS.VALIDATION_REQUIRED]:
                (requirePhoneNumber || requireThreeDsPhoneNumber) && !phoneNumber?.localNumber,
            [LOCALE_LABELS.VALIDATION_LOCAL_PHONE_NUMBER]:
                (requirePhoneNumber || requireThreeDsPhoneNumber) && validatePhoneNumber(phoneNumber),
        },
        "data.line1": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: isAddressRequired && isEmpty(line1),
            [LOCALE_LABELS.VALIDATION_INVALID_ADDRESS_LENGTH]: line1 && line1.length > 50,
        },
        "data.city": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: isAddressRequired && isEmpty(city),
            [LOCALE_LABELS.VALIDATION_INVALID_ADDRESS_LENGTH]: city && city.length > 50,
        },
        "data.state": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: isAddressRequired && isEmpty(state),
        },
        "data.zip": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: isAddressRequired && isEmpty(zip),
            [LOCALE_LABELS.VALIDATION_ZIP]: zip && !/^[-0-9]{7}/.test(zip),
            [LOCALE_LABELS.VALIDATION_INVALID_ZIP_LENGTH]: zip && zip.length > 16,
        },
        "data.country": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: isAddressRequired && !country,
        },
    });
};

const computeInlineStyle = (
    inlineStyles: Record<InlineStyleKeys, Record<string, string>>,
    label: InlineStyleKeys,
    error: InlineStyleKeys,
    invalid: InlineStyleKeys,
    focus: InlineStyleKeys,
    field: InlineStyleKeys,
    item: InlineStyleKeys
) =>
    inlineStyles
        ? {
              labelStyle: { ...inlineStyles[InlineStyleKeys.ITEM_LABEL], ...(inlineStyles[label] || {}) },
              errorStyle: { ...inlineStyles[InlineStyleKeys.ITEM_ERROR], ...(inlineStyles[error] || {}) },
              invalidStyle: { ...inlineStyles[InlineStyleKeys.FIELD_INVALID], ...(inlineStyles[invalid] || {}) },
              focusStyle: { ...inlineStyles[InlineStyleKeys.FIELD_FOCUS], ...(inlineStyles[focus] || {}) },
              inputStyle: { ...inlineStyles[InlineStyleKeys.FIELD], ...(inlineStyles[field] || {}) },
              style: { ...inlineStyles[InlineStyleKeys.ITEM], ...(inlineStyles[item] || {}) },
          }
        : {};

const Content = compose<OwnProps, OwnProps>(
    connect(stateSelector),
    reduxForm({
        form: FORM_CHECKOUT_NAME,
        validate,
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
        onChange: (values) => {
            if (!store.getState().loading.effects.application.clientUpdateParameters) {
                store.getState().application.splitCheckoutConnector?.update(values);
            }
        },
    })
)(
    ({
        handleSubmit,
        currency,
        subscriptionInitialAmount,
        isSubscription,
        tokens,
        selectedTokenId,
        selectedToken,
        fetched,
        requireEmail,
        inlineStyles,
        inlineBaseFontSize,
        showInstallmentSelect,
        darkTheme,
        horizontalInlineLayout,
        requirePhoneNumber,
        requireThreeDsPhoneNumber,
        requireAddress,
        requireThreeDsAddress,
        validationErrors,
        connector,
        isAcceptVisible,
        isCurrentBrandInstallmentCapable,
        installmentCycles = showInstallmentSelect ? 1 : undefined,
        cardBrand,
        hidePrivacyLink,
        callCenterTokenId,
        placeholderCardBrand,
        placeholderCardNumberLastFour,
        invalidFields,
        userData,
        updateFlowShouldCreateNewToken,
        requireCardholder,
        requireCardNumber,
        requireExpiry,
        requireCvv,
        requireName,
        requireNameKana,
    }) => {
        const { locale, formatMessage, formatNumber } = useIntl();

        const dispatch = useDispatch<Dispatch>();

        const {
            application: { clearInvalidFieldError, clearInvalidField },
            checkout: { process: processCheckout, setUpdateFlowShouldCreateNewToken },
            userData: { setUserData },
        } = dispatch;

        const handleSubmitForm = async (values: FormDataCard<CardDataRaw>) => {
            setUserData({
                data: {
                    line1: values.data?.line1 || undefined,
                    line2: values.data?.line2 || undefined,
                    city: values.data?.city || undefined,
                    state: values.data?.state || undefined,
                    zip: values.data?.zip || undefined,
                    country: values.data?.country || undefined,
                },

                phoneNumber: values.data?.phoneNumber,

                name: values?.name,
                nameKana: values?.nameKana,
            });

            const normalizedValues = normalizeValues(values, selectedToken) as FormCheckoutData;

            await processCheckout({
                values: {
                    ...normalizedValues,
                    installmentCycles: isCurrentBrandInstallmentCapable ? normalizedValues.installmentCycles : 1,
                },
            });
        };

        const textFieldCustomStyles = computeInlineStyle(
            inlineStyles,
            InlineStyleKeys.TEXT_ITEM_LABEL,
            InlineStyleKeys.TEXT_ITEM_ERROR,
            InlineStyleKeys.TEXT_FIELD_INVALID,
            InlineStyleKeys.TEXT_FIELD_FOCUS,
            InlineStyleKeys.TEXT_FIELD,
            InlineStyleKeys.TEXT_ITEM
        );

        const toggleFieldCustomStyles = computeInlineStyle(
            inlineStyles,
            InlineStyleKeys.TOGGLE_ITEM_LABEL,
            InlineStyleKeys.TOGGLE_ITEM_ERROR,
            InlineStyleKeys.TOGGLE_FIELD_INVALID,
            InlineStyleKeys.TOGGLE_FIELD_FOCUS,
            InlineStyleKeys.TOGGLE_FIELD,
            InlineStyleKeys.TOGGLE_ITEM
        );

        const selectFieldCustomStyles = computeInlineStyle(
            inlineStyles,
            InlineStyleKeys.SELECT_ITEM_LABEL,
            InlineStyleKeys.SELECT_ITEM_ERROR,
            InlineStyleKeys.SELECT_FIELD_INVALID,
            InlineStyleKeys.SELECT_FIELD_FOCUS,
            InlineStyleKeys.SELECT_FIELD,
            InlineStyleKeys.SELECT_ITEM
        );

        const radioFieldCustomStyles = computeInlineStyle(
            inlineStyles,
            InlineStyleKeys.RADIO_ITEM_LABEL,
            null,
            null,
            InlineStyleKeys.RADIO_FIELD_FOCUS,
            InlineStyleKeys.RADIO_ITEM,
            InlineStyleKeys.RADIO_ITEM
        );

        useEffect(() => {
            document.querySelector("html").style.fontSize = inlineBaseFontSize;
        }, [inlineBaseFontSize]);

        useEffect(() => {
            if (Object.keys(validationErrors?.data || {}).length) {
                connector.emitter.emit("checkout:validation-error", validationErrors);
            }
        }, [validationErrors]);

        const handleFormKeyPressed = (event: KeyboardEvent<HTMLFormElement>) => {
            if (event.key === "Enter") {
                event.preventDefault(); //<===== This stops the form from being submitted
                return false;
            }
        };

        const AddressComponent = locale.substring(0, 2).toLowerCase() === "ja" ? BillingAddressJa : BillingAddressEn;

        const onCardUpdateFieldChange = () => {
            clearInvalidField({ name: "cardNumber" });

            if (!callCenterTokenId || updateFlowShouldCreateNewToken) {
                return;
            }

            const affectedFields = ["data.cvv", "data.cardNumber", "data.exp"];
            affectedFields.forEach((field) => dispatch(blur(FORM_CHECKOUT_NAME, field, null)));
            setUpdateFlowShouldCreateNewToken({ updated: true });
        };

        const obfuscatedCardNumber =
            !updateFlowShouldCreateNewToken &&
            callCenterTokenId &&
            obfuscateCardNumber(placeholderCardBrand, placeholderCardNumberLastFour);
        const obfuscatedCvv =
            !updateFlowShouldCreateNewToken && callCenterTokenId && obfuscateCvv(placeholderCardBrand);

        return (
            <>
                <TestEnvironmentBanner />

                {tokens && getTokens(tokens, PaymentType.CARD).length > 0 && (
                    <FormCardSelector
                        inline
                        inlineStyles={{
                            textFieldCustomStyles,
                            toggleFieldCustomStyles,
                            selectFieldCustomStyles,
                            radioFieldCustomStyles,
                        }}
                    />
                )}

                <Form
                    onSubmit={handleSubmit(handleSubmitForm)}
                    onKeyPress={handleFormKeyPressed}
                    className={classnames("credit-card", "data", "content-form", { "dark-theme": darkTheme })}
                    noValidate>
                    {!fetched ? (
                        <SpinnerLoader />
                    ) : !selectedTokenId ? (
                        <>
                            {requireName && (
                                <TextField
                                    label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_NAME })}
                                    bsSize="sm"
                                    name="name"
                                    autoComplete="name1"
                                    tabIndex={3}
                                    required
                                    isDark={darkTheme}
                                />
                            )}

                            {requireNameKana && (
                                <TextField
                                    label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_NAME_KANA })}
                                    bsSize="sm"
                                    name="nameKana"
                                    autoComplete="name2"
                                    tabIndex={3}
                                    required
                                    isDark={darkTheme}
                                />
                            )}

                            {requireEmail && (
                                <TextField
                                    name="email"
                                    label={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_EMAIL_LABEL })}
                                    placeholder={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_EMAIL })}
                                    bsSize="sm"
                                    autoComplete="email"
                                    type="email"
                                    tabIndex={1}
                                    {...textFieldCustomStyles}
                                    required
                                    isDark={darkTheme}
                                    isHorizontal={horizontalInlineLayout}
                                    fieldError={invalidFields.email?.error}
                                    onChange={() => clearInvalidFieldError({ name: "email" })}
                                />
                            )}

                            <FormSection name="data">
                                {(requirePhoneNumber || requireThreeDsPhoneNumber) && (
                                    <PhoneNumberField
                                        label={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_PHONE_NUMBER_LABEL })}
                                        bsSize="sm"
                                        name="phoneNumber"
                                        autoComplete="phone"
                                        type="phone"
                                        tabIndex={2}
                                        required
                                        isDark={darkTheme}
                                        {...textFieldCustomStyles}
                                        isHorizontal={horizontalInlineLayout}
                                        fieldError={invalidFields.phoneNumber?.error}
                                        currentPhoneNumber={userData?.phoneNumber}
                                    />
                                )}

                                {requireCardholder && (
                                    <TextField
                                        name="cardholder"
                                        label={formatMessage({
                                            id: LOCALE_LABELS.FORM_CARD_FIELDS_CARDHOLDER_LABEL,
                                        })}
                                        placeholder={formatMessage({
                                            id: LOCALE_LABELS.FORM_CARD_FIELDS_CARDHOLDER,
                                        })}
                                        bsSize="sm"
                                        autoComplete="cc-name"
                                        tabIndex={3}
                                        normalize={(value) => value?.replace("　", " ")?.replace(/[0-9]/g, "")}
                                        {...textFieldCustomStyles}
                                        required
                                        isDark={darkTheme}
                                        isHorizontal={horizontalInlineLayout}
                                    />
                                )}

                                {requireCardNumber && (
                                    <TextField
                                        name="cardNumber"
                                        label={formatMessage({
                                            id: LOCALE_LABELS.FORM_CARD_FIELDS_CARD_NUMBER_LABEL,
                                        })}
                                        placeholder={
                                            updateFlowShouldCreateNewToken
                                                ? undefined
                                                : obfuscatedCardNumber ||
                                                  formatMessage({
                                                      id: LOCALE_LABELS.FORM_CARD_FIELDS_CARD_NUMBER,
                                                  })
                                        }
                                        bsSize="sm"
                                        tabIndex={4}
                                        onChange={onCardUpdateFieldChange}
                                        className={classnames({
                                            "match-text-placeholder":
                                                callCenterTokenId && !updateFlowShouldCreateNewToken,
                                        })}
                                        componentClass={CardNumberInput}
                                        {...textFieldCustomStyles}
                                        required
                                        isDark={darkTheme}
                                        isHorizontal={horizontalInlineLayout}
                                        fieldError={invalidFields.cardNumber?.error}
                                    />
                                )}

                                {requireExpiry && (
                                    <TextField
                                        name="exp"
                                        label={formatMessage({
                                            id: LOCALE_LABELS.FORM_CARD_FIELDS_EXPIRATION_DATE_LABEL,
                                        })}
                                        validationLabel={formatMessage({
                                            id: LOCALE_LABELS.FORM_CARD_FIELDS_EXPIRATION_DATE_LABEL_VALIDATION,
                                        })}
                                        bsSize="sm"
                                        tabIndex={5}
                                        width={112}
                                        componentClass={CardExpInput}
                                        showPlaceholder={!updateFlowShouldCreateNewToken}
                                        {...textFieldCustomStyles}
                                        required
                                        isDark={darkTheme}
                                        isHorizontal={horizontalInlineLayout}
                                    />
                                )}

                                {requireCvv && (
                                    <TextField
                                        name="cvv"
                                        label={formatMessage({ id: LOCALE_LABELS.FORM_CARD_FIELDS_CSC_LABEL })}
                                        placeholder={
                                            updateFlowShouldCreateNewToken
                                                ? undefined
                                                : obfuscatedCvv ||
                                                  formatMessage({ id: LOCALE_LABELS.FORM_CARD_FIELDS_CSC })
                                        }
                                        bsSize="sm"
                                        tabIndex={6}
                                        width={112}
                                        className={classnames({
                                            "match-text-placeholder":
                                                callCenterTokenId && !updateFlowShouldCreateNewToken,
                                        })}
                                        componentClass={CardCVVInput}
                                        {...textFieldCustomStyles}
                                        required
                                        isDark={darkTheme}
                                        isHorizontal={horizontalInlineLayout}
                                    />
                                )}
                            </FormSection>

                            {showInstallmentSelect &&
                                (isCurrentBrandInstallmentCapable || Number(installmentCycles) !== 1 ? (
                                    <InstallmentSelectField
                                        darkTheme={darkTheme}
                                        cardBrand={cardBrand}
                                        {...selectFieldCustomStyles}
                                        horizontal={horizontalInlineLayout}
                                    />
                                ) : (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip
                                                id={generate()}
                                                className={classnames("tooltip-small", { "light-theme": !darkTheme })}>
                                                {formatMessage(
                                                    { id: LOCALE_LABELS.FORM_INSTALLMENT_SELECT_HELPER },
                                                    { brand: cardBrand }
                                                )}
                                            </Tooltip>
                                        }>
                                        <div>
                                            <InstallmentSelectField
                                                darkTheme={darkTheme}
                                                cardBrand={cardBrand}
                                                {...selectFieldCustomStyles}
                                                horizontal={horizontalInlineLayout}
                                                disabled
                                            />
                                        </div>
                                    </OverlayTrigger>
                                ))}

                            {(requireAddress || requireThreeDsAddress) && (
                                <>
                                    <div className="section-title">
                                        {formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_TITLE })}
                                    </div>

                                    <AddressComponent
                                        staticCountry={undefined}
                                        isDark={darkTheme}
                                        textFieldCustomStyles={textFieldCustomStyles}
                                        selectFieldCustomStyles={selectFieldCustomStyles}
                                        invalidFields={invalidFields}
                                    />
                                </>
                            )}

                            {isAcceptVisible && (
                                <CheckboxField name="data.accept" {...toggleFieldCustomStyles} isDark={darkTheme}>
                                    {formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_ACCEPT_RECURRING })}
                                </CheckboxField>
                            )}

                            {isSubscription && !!subscriptionInitialAmount && (
                                <div className="text-left">
                                    {formatMessage(
                                        { id: LOCALE_LABELS.SUBSCRIPTIONS_INSTALLMENT_INITIAL_AMOUNT },
                                        {
                                            money: formatNumber(
                                                amountForCurrency(subscriptionInitialAmount, currency),
                                                {
                                                    style: "currency",
                                                    currency,
                                                }
                                            ),
                                        }
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {requireCvv && (
                                <>
                                    <FormSection name="data">
                                        <TextField
                                            name="cvv"
                                            label={formatMessage({ id: LOCALE_LABELS.FORM_CARD_FIELDS_CSC_LABEL })}
                                            placeholder={
                                                updateFlowShouldCreateNewToken
                                                    ? undefined
                                                    : formatMessage({ id: LOCALE_LABELS.FORM_CARD_FIELDS_CSC })
                                            }
                                            bsSize="sm"
                                            tabIndex={5}
                                            width={112}
                                            componentClass={CardCVVInput}
                                            {...textFieldCustomStyles}
                                            required
                                            isDark={darkTheme}
                                            isHorizontal={horizontalInlineLayout}
                                        />

                                        {(requirePhoneNumber || requireThreeDsPhoneNumber) && (
                                            <PhoneNumberField
                                                label={formatMessage({
                                                    id: LOCALE_LABELS.FORM_FIELDS_PHONE_NUMBER_LABEL,
                                                })}
                                                bsSize="sm"
                                                name="phoneNumber"
                                                autoComplete="phone"
                                                type="phone"
                                                tabIndex={2}
                                                required
                                                isDark={darkTheme}
                                                {...textFieldCustomStyles}
                                                isHorizontal={horizontalInlineLayout}
                                                fieldError={invalidFields.phoneNumber?.error}
                                                currentPhoneNumber={userData?.phoneNumber}
                                            />
                                        )}
                                    </FormSection>

                                    {(requireAddress ||
                                        (requireThreeDsAddress &&
                                            (selectedToken?.data as PatchedTokenCardData)?.threeDs?.status !==
                                                ThreeDsStatus.SUCCESSFUL)) && (
                                        <>
                                            <div className="section-title">
                                                {formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_TITLE })}
                                            </div>

                                            <AddressComponent
                                                staticCountry={undefined}
                                                isDark={darkTheme}
                                                invalidFields={invalidFields}
                                                {...textFieldCustomStyles}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {!hidePrivacyLink && <PrivacyLink className="inline-form-link" isDark={darkTheme} />}

                    <button type="submit" className="hidden" />
                </Form>
            </>
        );
    }
);

export default Content;
